import { useEffect, useRef, useState, type PointerEvent } from 'react'
import { siteMapImageUrl, type MapPlacement, type SiteMap } from '../api'
import { cameraLabel, describe, nameOf, positionOf, type CameraState } from '../siteMap'
import { SiteMapCone } from './SiteMapCone'

interface Props {
  siteId: string
  siteName: string
  map: SiteMap
  stateOf: (camera: string) => CameraState
  /** The camera singled out: watched beside live view, or picked on the Maps page. */
  current: string | null
  currentIs: 'watching' | 'picked'
  onSelect: (camera: string) => void
  /** The last line of a camera's card, saying what a click does. */
  hint: (camera: string) => string
  /** Names under the markers, where the plan has room for them. */
  labels?: boolean
  /** Scale, 1 fitting the plan to its frame. Above 1 the plan can be dragged. */
  zoom?: number
  /**
   * The tallest the plan may be drawn, as a CSS length. It is drawn to its own
   * shape within it, narrowing rather than being clipped: a tall plan beside
   * live view had its frame squeezed to what was left of the pane (#287).
   */
  maxHeight?: string
}

// SitePlan is a site's plan with its cameras on it — cones, markers and the
// card a pointed-at camera shows — for the site map beside live view and for
// the Maps page (#282), so the two cannot draw a camera differently.
export function SitePlan({ siteId, siteName, map, stateOf, current, currentIs, onSelect, hint, labels = false, zoom = 1, maxHeight }: Props) {
  // A cone's length is a share of the plan as drawn, so the plan is measured,
  // and again whenever its frame changes size.
  const [coneLayer, setConeLayer] = useState<HTMLDivElement | null>(null)
  const [planWidth, setPlanWidth] = useState(0)
  useEffect(() => {
    if (!coneLayer || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => setPlanWidth(entries[0].contentRect.width))
    observer.observe(coneLayer)
    return () => observer.disconnect()
  }, [coneLayer])
  // The camera under the pointer or keyboard focus, whose card is showing.
  const [pointed, setPointed] = useState<string | null>(null)

  // Dragged only once zoomed in, and kept so the plan never leaves its frame.
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const drag = useRef<{ x: number; y: number; from: { x: number; y: number } } | null>(null)
  // Eased while zooming, never while dragged: the transition made the plan
  // trail the pointer (#287).
  const [dragging, setDragging] = useState(false)
  const [frame, setFrame] = useState<HTMLDivElement | null>(null)
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    if (!frame || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      setFrameSize({ width, height })
    })
    observer.observe(frame)
    return () => observer.disconnect()
  }, [frame])
  const clamp = (o: { x: number; y: number }) => {
    if (zoom <= 1) return { x: 0, y: 0 }
    const maxX = (frameSize.width * (zoom - 1)) / 2
    const maxY = (frameSize.height * (zoom - 1)) / 2
    return { x: Math.max(-maxX, Math.min(maxX, o.x)), y: Math.max(-maxY, Math.min(maxY, o.y)) }
  }
  const shown = clamp(offset)
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (zoom <= 1 || (e.target as HTMLElement).closest('button')) return
    drag.current = { x: e.clientX, y: e.clientY, from: shown }
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (d) setOffset(clamp({ x: d.from.x + e.clientX - d.x, y: d.from.y + e.clientY - d.y }))
  }
  const endDrag = () => {
    drag.current = null
    setDragging(false)
  }

  const pointedAt = map.placements.find((q) => q.camera === pointed)
  return (
    <div
      ref={setFrame}
      role="group"
      aria-label={`Cameras on the plan of ${siteName}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={maxHeight && map.width && map.height ? { aspectRatio: `${map.width} / ${map.height}`, width: `min(100%, calc(${maxHeight} * ${map.width} / ${map.height}))` } : undefined}
      className={`relative shrink-0 mx-auto overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 ${zoom > 1 ? 'cursor-grab active:cursor-grabbing touch-none' : ''}`}
    >
      <div
        style={{ transform: `translate(${shown.x}px, ${shown.y}px) scale(${zoom})` }}
        className={`relative origin-center ${dragging ? '' : 'transition-transform duration-150 ease-out motion-reduce:transition-none'}`}
      >
        <img
          src={siteMapImageUrl(siteId, map.updated_at)}
          alt={`Plan of ${siteName}`}
          className="block w-full h-auto select-none"
          draggable={false}
        />
        {/* Cones are clipped to the plan; the card must not be, so the two layers differ. */}
        <div ref={setConeLayer} className="absolute inset-0 overflow-hidden pointer-events-none">
          {map.placements.map((p) => (
            <SiteMapCone key={`cone-${p.camera}`} placement={p} tone={p.camera === current ? 'highlight' : 'muted'} planWidth={planWidth} />
          ))}
        </div>
        {map.placements.map((p) => (
          <Marker
            key={p.camera}
            placement={p}
            state={stateOf(p.camera)}
            current={p.camera === current}
            currentIs={currentIs}
            label={labels}
            onSelect={() => onSelect(p.camera)}
            onPoint={(on) => setPointed((was) => (on ? p.camera : was === p.camera ? null : was))}
          />
        ))}
        {pointedAt && <CameraCard placement={pointedAt} state={stateOf(pointedAt.camera)} hint={hint(pointedAt.camera)} />}
      </div>
    </div>
  )
}

function Marker({ placement: p, state, current, currentIs, label, onSelect, onPoint }: {
  placement: MapPlacement
  state: CameraState
  current: boolean
  currentIs: 'watching' | 'picked'
  label: boolean
  onSelect: () => void
  onPoint: (on: boolean) => void
}) {
  const ring = current
    ? 'border-orange-300 bg-orange-400 text-zinc-950 shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-orange-500)_25%,transparent)]'
    : state.health === 'offline'
      ? 'border-fault bg-zinc-900 text-fault'
      : state.health === 'degraded'
        ? 'border-dashed border-degraded bg-zinc-900 text-zinc-200'
        : 'border-zinc-500 bg-zinc-900 text-zinc-200'
  const watching = current && currentIs === 'watching'
  return (
    <>
      <button
        type="button"
        aria-label={cameraLabel(p.camera, p.place, state, watching)}
        aria-current={watching ? 'true' : undefined}
        aria-pressed={currentIs === 'picked' ? current : undefined}
        onClick={onSelect}
        onMouseEnter={() => onPoint(true)}
        onMouseLeave={() => onPoint(false)}
        onFocus={() => onPoint(true)}
        onBlur={() => onPoint(false)}
        style={positionOf(p)}
        className={`absolute -translate-x-1/2 -translate-y-1/2 w-[30px] h-[30px] rounded-full border-2 flex items-center justify-center ${ring}`}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path d="M15 10l5-3v10l-5-3M4 7h11v10H4z" />
          {/* A shape as well as a colour, so offline is not told by red alone. */}
          {state.health === 'offline' && <path d="M3 3l18 18" />}
        </svg>
        {state.inProgress && (
          <span aria-hidden className="absolute -top-[3px] -left-[3px] w-[9px] h-[9px] rounded-full bg-live border-2 border-zinc-900 animate-pulse motion-reduce:animate-none" />
        )}
        {state.tamper && (
          <span aria-hidden className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-attention text-zinc-950 text-[10px] font-extrabold leading-[14px] text-center">!</span>
        )}
      </button>
      {label && (
        <span
          aria-hidden
          style={{ left: positionOf(p).left, top: `calc(${positionOf(p).top} + 20px)` }}
          className="absolute -translate-x-1/2 px-1.5 py-0.5 rounded bg-black/65 text-xs font-semibold text-white whitespace-nowrap pointer-events-none"
        >
          {nameOf(p.camera, p.place)}
        </span>
      )}
    </>
  )
}

// Centred on its camera but held inside the plan, and below the camera in the
// plan's upper half, above it in the lower, so a camera near an edge keeps its
// card in view.
function CameraCard({ placement: p, state, hint }: { placement: MapPlacement; state: CameraState; hint: string }) {
  const words = describe(state)
  const tone = state.health === 'offline' ? 'text-fault' : state.tamper ? 'text-attention' : 'text-zinc-300'
  const style = {
    left: `clamp(0px, calc(${p.x * 100}% - 88px), calc(100% - 176px))`,
    ...(p.y > 0.5 ? { bottom: `calc(${(1 - p.y) * 100}% + 22px)` } : { top: `calc(${p.y * 100}% + 22px)` }),
  }
  return (
    <div
      role="tooltip"
      style={style}
      className="absolute z-10 w-44 flex flex-col gap-1 px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 shadow-[0_10px_24px_rgb(0_0_0/0.5)] pointer-events-none"
    >
      <span className="text-[13px] font-semibold text-zinc-100">{nameOf(p.camera, p.place)}</span>
      {words.length > 0 && <span className={`text-xs ${tone}`}>{words.join(', ')}</span>}
      <span className="text-[11px] text-zinc-400 border-t border-zinc-700 pt-1 mt-0.5">{hint}</span>
    </div>
  )
}
